import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { CheckCircle, Star, Warning } from '@mui/icons-material';
import { useSubscription } from '@/hooks/useSubscription';
import { brandColors } from '@/theme/colors';
import { PRO_PRICING, formatPrice } from '@/types/subscription';

type UpgradeDialogProps = {
  open: boolean;
  onClose: () => void;
};

const benefits = [
  'Unlimited ThinkSpaces',
  'Unlimited ToDo lists',
  'Unlimited Notebooks',
  'Larger storage limits',
  'Priority saves',
  'Export (PDF / image / markdown)',
  'Share with team members',
];

export const UpgradeDialog = ({ open, onClose }: UpgradeDialogProps) => {
  const {
    data,
    isLoading,
    isPro,
    isCanceling,
    isPastDue,
    upgrade,
    manageSubscription,
    upgrading,
    managing,
    error: actionError,
  } = useSubscription();

  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async () => {
    setError(null);
    try {
      await upgrade();
    } catch (err) {
      setError('Failed to start checkout. Please try again.');
    }
  };

  const handleManage = async () => {
    setError(null);
    try {
      await manageSubscription();
    } catch (err) {
      setError('Failed to open subscription portal. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const displayError = error || actionError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isPro ? 'Your Subscription' : 'Upgrade to Pro'}
      </DialogTitle>
      <DialogContent>
        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {displayError}
          </Alert>
        )}

        {isPro ? (
          // Pro user view
          <Box>
            <Chip
              icon={<Star />}
              label="Pro Plan"
              sx={{
                mb: 2,
                background: `linear-gradient(135deg, ${brandColors.pink} 0%, ${brandColors.purple} 100%)`,
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />

            <Typography variant="body1" gutterBottom>
              You're on the Pro plan with unlimited access to all features.
            </Typography>

            {data?.currentPeriodEnd && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isCanceling ? 'Access until' : 'Next billing date'}:{' '}
                <strong>{data.currentPeriodEnd.toLocaleDateString()}</strong>
              </Typography>
            )}

            {isCanceling && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                Your subscription is set to cancel at the end of the billing period.
                You can reactivate it from the subscription portal.
              </Alert>
            )}

            {isPastDue && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Your payment failed. Please update your payment method to continue
                using Pro features.
              </Alert>
            )}
          </Box>
        ) : (
          // Free user view - show upgrade offer
          <Box>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${brandColors.purple} 0%, ${brandColors.pink} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {formatPrice(PRO_PRICING.monthly)}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                per month, billed annually ({formatPrice(PRO_PRICING.annual)}/year)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                NZ customers: +15% GST at checkout
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Everything in Free, plus:
            </Typography>

            <Stack spacing={1.5}>
              {benefits.map((benefit, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                  <CheckCircle
                    sx={{ color: brandColors.purple, fontSize: 20 }}
                  />
                  <Typography variant="body2">{benefit}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} disabled={upgrading || managing}>
          Close
        </Button>
        {isPro ? (
          <Button
            variant="outlined"
            onClick={handleManage}
            disabled={managing}
            startIcon={managing ? <CircularProgress size={16} /> : undefined}
          >
            {managing ? 'Opening...' : 'Manage Subscription'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleUpgrade}
            disabled={upgrading}
            startIcon={upgrading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              background: `linear-gradient(135deg, ${brandColors.pink} 0%, ${brandColors.purple} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${brandColors.pink} 0%, ${brandColors.purpleDeep} 100%)`,
              },
            }}
          >
            {upgrading ? 'Redirecting...' : 'Upgrade Now'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
